/**
 * =========================================================================================
 * HRMS - Tooltips
 * =========================================================================================
 **/

import React from 'react';
import Popup from 'reactjs-popup';
import PropTypes from 'prop-types';
import UAvatar from '../UAvatar';
import { getName } from '../common/function';
import UPopup from "ubs-framework/v2/UPopup";
import { LIMIT } from '../common/constant';

/**
 * UTooltip — combined component supporting all variant features:
 * - src      : basic text + on trigger
 * - chat     : tooltipRef, position, arrow, offsetX, zIndex
 * - pms      : tooltipRef, position, className, disabled, params spread, isHTML
 * - recruit  : same as pms
 * - discussion: minimal, uses className for module-specific class
 */
const UTooltip = React.memo((props) => {
    return (
        <Popup
            ref={props.tooltipRef || props.propRef}
            nested
            trigger={open => props.on(open)}
            arrow={props.arrow === false ? false : true}
            on={['hover', 'focus']}
            position={props.position ? props.position : 'bottom center'}
            closeOnDocumentClick
            repositionOnResize
            offsetX={props.offsetX}
            keepTooltipInside={props.keepTooltipInside ? props.keepTooltipInside : true}
            mouseEnterDelay={props.mouseEnterDelay ?? LIMIT.TOOLTIP_DELAY}
            className={`u_tooltip ${props.className || ''}`}
            disabled={props?.disabled ? props?.disabled : false}
            contentStyle={{ zIndex: props?.zIndex }}
            overlayStyle={{ zIndex: props?.zIndex }}
            {...props?.params}
        >
            {props?.isHTML
                ? <span dangerouslySetInnerHTML={{ __html: props.text }} />
                : <span>{props.text}</span>}
        </Popup>
    );
});

UTooltip.propTypes = {
    on: PropTypes.func,
    text: PropTypes.node,
    tooltipRef: PropTypes.any,
    propRef: PropTypes.any,
    position: PropTypes.string,
    arrow: PropTypes.bool,
    offsetX: PropTypes.number,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    params: PropTypes.object,
    isHTML: PropTypes.bool,
    mouseEnterDelay: PropTypes.number,
    zIndex: PropTypes.number,
    keepTooltipInside: PropTypes.any,
};

UTooltip.defaultProps = {
    on: () => {},
    text: '',
    className: '',
    disabled: false,
    zIndex: 1001,
};

export default UTooltip;

export const listTooltip = (users, s3_url) => {
    return (
        <div className="u_flex u_align-center">
            {
                users?.length ?
                    <>
                        <span className="u_one-line u_table-one-line">
                            {getName(users?.[0])}
                        </span>
                        {
                            users?.length > 1 &&
                            <UPopup
                                params={{ className: "u_tooltip u_tooltip_more", position: 'bottom right', on: ['click'] }}
                                desc={<div className="u_popover-more-text">
                                    <div className="u_popover-more-text-inner">
                                        {
                                            users?.slice(1).map((user, index) => {
                                                return <div className={`u_flex u_align-center ${users?.slice(1)?.length - 1 === index ? '' : 'u_mb-10'}`}>
                                                    <div className="u_flex-shrink">
                                                        <UAvatar
                                                            name={getName(user)}
                                                            width={25}
                                                            // thumbor={getThumbor('32x32')}
                                                            image={user?.profileImage}
                                                            s3Url={s3_url}
                                                        />
                                                    </div>
                                                    <p className="u_ml-5">{getName(user)}</p>
                                                </div>
                                            })
                                        }
                                    </div>
                                </div>}
                            >
                                {
                                    users?.length > 1 &&
                                    <span className="u_table-list-total u_cp">
                                        {users?.length - 1}+
                                    </span>
                                }
                            </UPopup>
                        }
                    </>
                    : '-'
            }
        </div>
    );
}

// For Users List
export const errorListTooltip = (users) => {
    return (
        <div className="u_flex u_align-center">
            {
                users?.length ?
                    <>
                        <span className="u_one-line u_table-one-line">
                            {users?.[0]?.name || users?.[0]}
                        </span>
                        {
                            users?.length > 1 &&
                            <UPopup
                                params={{ className: "u_tooltip u_tooltip_more", position: 'bottom right', on: ['click'] }}
                                desc={<div className="u_popover-more-text" style={{ width: 200 }}>
                                    <div className="u_popover-more-text-inner">
                                        {
                                            users?.map((user, index) => {
                                                if (index > 0) {
                                                    return <p className={`u_text-c u_mb-10`}>{user?.name || users[index]}</p>
                                                }
                                            })
                                        }
                                    </div>
                                </div>}
                            >
                                {
                                    users?.length > 1 &&
                                    <span className="u_table-list-total u_cp">
                                        {users?.length - 1}+
                                    </span>
                                }
                            </UPopup>
                        }
                    </>
                    : '-'
            }
        </div>
    );
}

// For Activity Log List
export const logTooltip = (list) => {
    return (
        <div className="u_table-activity-list u_flex u_align-center">
            <p>{list[0].label}: {list[0].value}</p>
            <UPopup
                params={{ className: "u_tooltip_more u_tooltip-list" }}
                desc={<div className="u_popover-more-text u_table-activity-list">
                    {
                        list?.slice(1).map(item => {
                            return <p>{item.label}: {item.value}</p>
                        })
                    }
                </div>}
            >
                {
                    list?.length > 1 &&
                    <span className="u_table-list-total u_cp">
                        {list?.length - 1}+ MORE
                    </span>
                }
            </UPopup>
        </div>
    );
}

// Show more text
export const showMoreText = (text, text_length = 40) => {

    let more_text = text?.length > text_length ? text?.substr(0, text_length - 1) : text;
    let f_text = window.innerWidth < 1366 ? more_text?.substr(0, 40) : more_text;

    return (
        <React.Fragment>
            <span className="u_two-line u_table-two-line u_word-break-word">
                {f_text}
                {text?.length > 40 &&
                    <UPopup
                        params={{ className: "u_tooltip u_tooltip_more", position: 'bottom center', defaultOpen: true }}
                        desc={<div className="u_popover-more-text">
                            <div className="u_popover-more-text-inner">
                                <span>{text}</span>
                            </div>
                        </div>}
                    >
                        <span className="u_table-more-text u_cp">...More</span>
                    </UPopup>}
            </span>
        </React.Fragment>
    );
}
